import { useState, memo } from "react";
import { useTranslation } from "react-i18next";

import moreFilterOptionsIcon from "/assets/more-filter-options.svg";
import InlineSelect from "/components/InlineSelect";

const Filter = memo(({
  label,
  options,
  onChange,
  selectedOptions = [],
  featuredOptions = [],
  initialOptionsAmount = 3,
}) => {
  const { t } = useTranslation();
  const [isAllOptionsVisible, setIsAllOptionsVisible] = useState(false);

  const handleShowMoreOptions = (event) => {
    event.preventDefault();

    setIsAllOptionsVisible(true);
  };

  const handleShowLessOptions = (event) => {
    event.preventDefault();

    setIsAllOptionsVisible(false);
  };

  const allOptions = Object.entries(options);

  // Nothing in the list carries this dictionary — a label above a lone "All"
  // filters nothing, so the whole block stays out of the page.
  if (allOptions.length === 0) return null;

  // Collapsed, the filter shows the options featured in the admin; with none of
  // them featured it falls back to the first few, whatever they are. Option keys
  // come out of the options object as strings, so the featured ids are read as
  // strings too.
  const featuredKeys = featuredOptions.map(String);
  const isFeatured = ([key]) => featuredKeys.includes(key);

  // The featured ones lead the expanded list as well, so expanding only appends
  // below what was already on screen instead of reshuffling it.
  const orderedOptions = featuredKeys.length > 0
    ? [...allOptions.filter(isFeatured), ...allOptions.filter(option => !isFeatured(option))]
    : allOptions;
  const collapsedOptions = featuredKeys.length > 0
    ? orderedOptions.filter(isFeatured)
    : orderedOptions.slice(0, initialOptionsAmount);

  const visibleOptions = isAllOptionsVisible ? orderedOptions : collapsedOptions;
  const hiddenOptionsAmount = allOptions.length - collapsedOptions.length;

  const handleChange = (event, key) => {
    if (typeof onChange !== "function") return;

    let newValue;

    if (event.target.checked) {
      newValue = [...selectedOptions, key];
    } else {
      newValue = selectedOptions.filter(optionKey => optionKey !== key);
    }

    onChange(newValue);
  };

  const handleReset = (event) => {
    if (selectedOptions.length > 0) {
      onChange([]);
    } else {
      setIsAllOptionsVisible(true);
      onChange(Object.keys(options));
    }
  };

  const allOptionsAreSelected = selectedOptions.length === Object.keys(options).length;

  return (
    <>
      <div className="projects-filter--desktop">
        <label className="projects-filter__label">{label}</label>
        <ul className="projects-filter__options">
          <li key="all">
            <label className="chck__label">
              <input
                type="checkbox"
                className="chck"
                onClick={handleReset}
                checked={allOptionsAreSelected}
                readOnly={true}
              />
              {t("projects.filters.all")}
            </label>
          </li>

          {visibleOptions.map(([key, value]) => (
            <li key={key}>
              <label className="chck__label">
                <input
                  type="checkbox"
                  className="chck"
                  checked={selectedOptions.includes(key)}
                  onChange={(event) => handleChange(event, key)}
                />
                {value}
              </label>
            </li>
          ))}
        </ul>

        {(!isAllOptionsVisible && hiddenOptionsAmount > 0) && (
          <a
            className="projects-filter__more-options"
            href="#"
            onClick={handleShowMoreOptions}
          >
            {t("projects.filters.more", { amount: hiddenOptionsAmount })} <img src={moreFilterOptionsIcon} />
          </a>
        )}

        {(isAllOptionsVisible && hiddenOptionsAmount > 0) && (
          <a
            className="projects-filter__more-options--less"
            href="#"
            onClick={handleShowLessOptions}
          >
            {t("projects.filters.less")} <img src={moreFilterOptionsIcon} />
          </a>
        )}
      </div>

      <div className="projects-filter--mobile">
        <InlineSelect
          label={label}
          options={options}
          onChange={onChange}
          selectedOptions={selectedOptions}
        />
      </div>
    </>
  );
});

export default Filter;
